import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { musicServicesByMusicAPIID } from 'fym-shared/src/music-services/allMusicServices';
import { TypedMemo } from '../../common/types';
import { PresaveCampaignStats } from '../../../redux/api/fymAPI.types';
import { PresaveCounterCard } from './PresaveCounterCard';
import { PresaveServiceRow } from './PresaveServiceRow';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole panel; each part uses its own slice of it */
import styles from './PresavePanel.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PresaveSavesCardProps {
  stats: PresaveCampaignStats;
  /** The services the block offers, so a service with nobody on it still shows. */
  services: string[];
}

/**
 * How many fans are waiting, and on which service. The per-service split is the
 * denominator the old two-line list never gave: one number tells the artist
 * nothing about where to spend the next post.
 */
const PresaveSavesCardComponent: React.FC<PresaveSavesCardProps> = ({ stats, services }) => {
  const intl = useIntl();

  const rows = useMemo(() => {
    const counts = new Map(stats.byService.map((row) => [row.integrationType, row.count]));
    // The offered services first, then anything counted that is no longer
    // offered — an artist can drop a service from the block after a fan used it.
    const keys = [
      ...services,
      ...stats.byService.map((row) => row.integrationType).filter((id) => !services.includes(id)),
    ];
    const max = Math.max(1, ...counts.values());
    return keys.map((integrationType) => {
      const count = counts.get(integrationType) ?? 0;
      return {
        integrationType,
        count,
        share: count / max,
        label: musicServicesByMusicAPIID[integrationType]?.name ?? integrationType,
      };
    });
  }, [services, stats.byService]);

  const total = useMemo(() => stats.byService.reduce((sum, row) => sum + row.count, 0), [stats.byService]);

  return (
    <PresaveCounterCard
      label={intl.formatMessage({ id: 'app.sml.presave.panel.saves', defaultMessage: 'Pre-saves' })}
      value={intl.formatNumber(total)}
      delta={
        stats.todayCount > 0
          ? intl.formatMessage(
              { id: 'app.sml.presave.panel.today', defaultMessage: '+{count} today' },
              { count: intl.formatNumber(stats.todayCount) }
            )
          : '—'
      }
      deltaIsPositive={stats.todayCount > 0}
    >
      {rows.length > 0 && (
        <div className={styles.services}>
          {rows.map((row) => (
            <PresaveServiceRow
              key={row.integrationType}
              integrationType={row.integrationType}
              label={row.label}
              count={row.count}
              share={row.share}
            />
          ))}
        </div>
      )}
    </PresaveCounterCard>
  );
};

export const PresaveSavesCard = TypedMemo(PresaveSavesCardComponent);
